import { ReactElement, useMemo } from 'react';
import {
    UIErrorState,
    UILoadingState,
    UISubSidebar,
    UISubSidebarGroupProps,
    UISubSidebarItemProps
} from '@/ui';
import { Outlet } from 'react-router';
import { useFetchCompany } from '@/companies/queries';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';
import { useCompanyContext } from '@/companies/providers';
import { useScrollToTopOnNavigate } from '@/common/hooks';

export function SettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const companyId = useCompanyContext().company.id;
    const { status, company, refetch } = useFetchCompany(companyId);

    // This element outlives the pages inside it, so it keeps its scroll
    // position across a navigation unless it is sent back to the top.
    const scrollRef = useScrollToTopOnNavigate<HTMLDivElement>();

    const {
        canViewGeneralSettings,
        canViewBilling,
        canViewTeams,
        canViewMembers,
        canViewBeacons,
        canViewDevices
    } = useCompanyPermissions();

    const absolutePath = `/company/${companyId}/settings`;
    const sideBarItems: UISubSidebarItemProps[] = useMemo(() => {
        const items: UISubSidebarItemProps[] = [];

        if (canViewGeneralSettings) {
            items.push({
                text: t('shared.labels.general'),
                link: 'general',
                additionalActiveLinkPaths: [`/company/${companyId}/settings`]
            });
        }

        if (canViewBilling) {
            items.push({
                text: t('shared.labels.plan_and_billing'),
                link: 'billing'
            });
        }

        if (canViewMembers) {
            items.push({
                text: t('shared.labels.members'),
                link: 'members'
            });
        }

        if (canViewTeams) {
            items.push({
                text: t('shared.labels.teams'),
                link: 'teams'
            });
        }

        if (canViewDevices) {
            items.push({
                text: t('shared.labels.devices'),
                link: 'devices'
            });
        }

        if (canViewBeacons) {
            items.push({
                text: t('shared.labels.beacons'),
                link: 'beacons'
            });
        }

        return items;
    }, [
        canViewGeneralSettings,
        canViewBilling,
        canViewTeams,
        canViewMembers,
        canViewBeacons,
        canViewDevices,
        companyId,
        t
    ]);

    const groups: UISubSidebarGroupProps[] = useMemo(() => {
        return [
            {
                title: 'COMPANY SETTINGS',
                items: sideBarItems
            },
            {
                title: 'USER SETTINGS',
                items: [
                    {
                        text: t('shared.labels.general_settings'),
                        link: '/account'
                    }
                ]
            }
        ];
    }, [sideBarItems, t]);

    return (
        <div style={{ display: 'flex', flex: 1 }}>
            <UISubSidebar absolutePath={absolutePath} groups={groups} />
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    position: 'relative'
                }}
            >
                {status === 'pending' && <UILoadingState />}
                {status === 'error' && (
                    <UIErrorState
                        title={t('shared.errors.load_settings')}
                        onRetry={refetch}
                    />
                )}
                {status === 'success' && company && (
                    <div
                        ref={scrollRef}
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            overflow: 'auto'
                        }}
                    >
                        <div
                            style={{ display: 'flex', flex: 1, maxWidth: 1000 }}
                        >
                            <Outlet />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
